import { useSyncExternalStore } from "react";
import { isNative } from "./native";

/**
 * What kind of machine is looking at this page.
 *
 * The product treats a phone very differently from a tablet: a phone is sent to
 * the app-download screen, a tablet gets the full desktop experience. That makes
 * the classification a product decision rather than a styling one, so it is
 * deliberately *not* derived from `useBreakpoint` — a narrow desktop window is
 * still a desktop, and an iPad at 768 CSS pixels is still a full workstation for
 * this product's purposes.
 *
 * Layout continues to use `useBreakpoint`. This module answers a different
 * question and is used only by the device gate.
 */
export type DeviceClass = "phone" | "tablet" | "desktop";

/**
 * The largest "smallest viewport side", in CSS pixels, a phone is assumed to
 * have. Every phone in common use is below this in both orientations; the
 * smallest tablets (iPad mini at 744, 8" Android tablets at 800) are above it.
 */
export const PHONE_MAX_SMALLEST_SIDE = 600;

export type DeviceSignals = {
  innerWidth: number;
  innerHeight: number;
  /** `(pointer: coarse)` — the primary input is a finger, not a mouse. */
  coarsePointer: boolean;
  /** `(hover: hover)` — the primary input can rest on a target without pressing. */
  hoverCapable: boolean;
  /**
   * `navigator.userAgentData.mobile`. Chromium-based browsers publish this and
   * it is the only first-class "this is a phone" signal that exists; Safari and
   * Firefox do not, hence the user-agent fallback.
   */
  userAgentDataMobile?: boolean | null;
  userAgent?: string;
  maxTouchPoints?: number;
  /** The Capacitor/Tauri shell embeds this same web UI and is never gated. */
  native?: boolean;
};

/**
 * Tablets, including the ones that hide it.
 *
 * `Android` without `Mobile` is the documented way Android tablets identify
 * themselves. iPadOS is the awkward case: since iPadOS 13 Safari sends a
 * desktop macOS user agent, so `classifyDevice` also treats a "Macintosh" that
 * reports touch points as an iPad.
 */
const TABLET_USER_AGENT = /\biPad\b|\bTablet\b|\bPlayBook\b|\bKindle\b|Android(?!.*\bMobile\b)/i;

const PHONE_USER_AGENT =
  /\biPhone\b|\biPod\b|Android.*\bMobile\b|\bWindows Phone\b|\bIEMobile\b|\bBlackBerry\b|\bBB10\b|\bOpera Mini\b/i;

function looksLikeTablet(signals: DeviceSignals): boolean {
  const userAgent = signals.userAgent ?? "";
  if (TABLET_USER_AGENT.test(userAgent)) return true;
  return /\bMacintosh\b/i.test(userAgent) && (signals.maxTouchPoints ?? 0) > 1;
}

/**
 * Decide the device class from already-read signals.
 *
 * Pure on purpose: every branch below is a real device somebody will arrive on,
 * and the only way to keep them all correct is to be able to assert them.
 *
 * The order matters:
 *
 * 1. The native shell is never a phone *for gating purposes* — it embeds this
 *    UI behind its own chrome and its own OAuth session, so blocking it would
 *    block the very app the gate is advertising.
 * 2. Anything that can hover with a fine pointer is a desktop, regardless of
 *    how narrow the window has been dragged. This is what stops a 500px-wide
 *    desktop browser window from being mistaken for a phone.
 * 3. A touch device that announces itself as a tablet is a tablet even when the
 *    viewport is narrow, because iPad Split View and Android split-screen can
 *    both report a very small width next to a full-height screen.
 * 4. Otherwise the smallest viewport side decides, cross-checked against the
 *    user agent.
 */
export function classifyDevice(signals: DeviceSignals): DeviceClass {
  if (signals.native) return "desktop";

  const handheld = signals.coarsePointer && !signals.hoverCapable;
  if (!handheld) return "desktop";

  if (looksLikeTablet(signals)) return "tablet";

  const smallestSide = Math.min(signals.innerWidth, signals.innerHeight);
  if (smallestSide >= PHONE_MAX_SMALLEST_SIDE) return "tablet";

  // Client Hints are authoritative where they exist: `mobile: false` on a small
  // touch screen means a tablet or a kiosk, never a phone.
  if (signals.userAgentDataMobile === false) return "tablet";
  if (signals.userAgentDataMobile === true) return "phone";

  // No Client Hints (Safari, Firefox). The user agent is the last cross-check,
  // and an unrecognised handheld is deliberately *not* treated as a phone: the
  // cost of a wrong "phone" is locking somebody out of the whole product, while
  // the cost of a wrong "tablet" is only a cramped layout.
  return PHONE_USER_AGENT.test(signals.userAgent ?? "") ? "phone" : "tablet";
}

type UserAgentDataCapableNavigator = Navigator & {
  userAgentData?: { mobile?: boolean };
};

let coarsePointerQuery: MediaQueryList | undefined;
let hoverQuery: MediaQueryList | undefined;

function mediaQueries(): [MediaQueryList, MediaQueryList] {
  coarsePointerQuery ??= window.matchMedia("(pointer: coarse)");
  hoverQuery ??= window.matchMedia("(hover: hover)");
  return [coarsePointerQuery, hoverQuery];
}

export function readDeviceSignals(): DeviceSignals {
  const [coarse, hover] = mediaQueries();
  const agent = navigator as UserAgentDataCapableNavigator;
  return {
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    coarsePointer: coarse.matches,
    hoverCapable: hover.matches,
    userAgentDataMobile: agent.userAgentData?.mobile ?? null,
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    native: isNative(),
  };
}

/** The device class right now, for code that runs outside React (route guards). */
export function getDeviceClass(): DeviceClass {
  if (typeof window === "undefined") return "desktop";
  try {
    return classifyDevice(readDeviceSignals());
  } catch {
    // A browser without `matchMedia` is not a reason to lock somebody out.
    return "desktop";
  }
}

/**
 * Rotation, resizes and an external display being attached all change the
 * answer, so the subscription covers the media queries as well as the window.
 */
function subscribe(onChange: () => void): () => void {
  const [coarse, hover] = mediaQueries();
  window.addEventListener("resize", onChange);
  window.addEventListener("orientationchange", onChange);
  coarse.addEventListener("change", onChange);
  hover.addEventListener("change", onChange);
  return () => {
    window.removeEventListener("resize", onChange);
    window.removeEventListener("orientationchange", onChange);
    coarse.removeEventListener("change", onChange);
    hover.removeEventListener("change", onChange);
  };
}

export function useDeviceClass(): DeviceClass {
  return useSyncExternalStore(subscribe, getDeviceClass, () => "desktop" as DeviceClass);
}

/** Test seam: media-query handles are cached for the lifetime of the document. */
export function resetDeviceClassCacheForTests(): void {
  coarsePointerQuery = undefined;
  hoverQuery = undefined;
}
